import { createContext, useContext, useEffect, useState, type RefObject } from 'react';
import { fetchSiteContent } from '../../api/site';
import {
  DEFAULT_CONTENT, SiteContent, SiteShot, resolveDoc, shotOf, shotsById,
} from '../../shared/siteContent';

/**
 * The public site's words, look and screenshots, as Site content published
 * them (078) — read once per page load and shared by every page that draws
 * them: the marketing pages through SiteLayout's context, the sign-in and
 * sign-up asides directly.
 *
 * A page holds its first paint for the read (up to PATIENCE_MS) rather than
 * draw the launch copy and swap it a moment later — a headline that changes
 * while it is being read is worse than a blank tenth of a second. A read
 * that is slower than that, or fails, draws the launch copy
 * (shared/siteContent's DEFAULT_CONTENT), and a late answer still replaces it.
 */
const PATIENCE_MS = 1500;

let loading: Promise<SiteContent> | null = null;
let loaded: SiteContent | null = null;

const isShot = (s: unknown): s is SiteShot => {
  const x = s as SiteShot | null;
  return !!x && typeof x.id === 'string' && typeof x.file === 'string' && typeof x.desktop?.src === 'string';
};

/** The library from the wire, or the fallback's when there is none worth drawing. */
export function toShots(raw: unknown): Record<string, SiteShot> {
  const list = Array.isArray(raw) ? raw.filter(isShot) : [];
  return list.length ? shotsById(list) : DEFAULT_CONTENT.shots;
}

export function loadSiteContent(): Promise<SiteContent> {
  if (!loading) {
    loading = fetchSiteContent()
      .then((r) => ({ doc: resolveDoc(r?.content?.doc), shots: toShots(r?.content?.shots) }))
      .catch(() => DEFAULT_CONTENT)
      .then((c) => { loaded = c; return c; });
  }
  return loading;
}

/** Forget what was read, so the next page asks again — after a publish, and between tests. */
export function invalidateSiteContent() {
  loading = null;
  loaded = null;
}

/** The published content; undefined only while the first read is out and not yet overdue. */
export function useSiteContent(): SiteContent | undefined {
  const [content, setContent] = useState<SiteContent | undefined>(() => loaded ?? undefined);
  useEffect(() => {
    let alive = true;
    const late = setTimeout(() => { if (alive) setContent((c) => c ?? DEFAULT_CONTENT); }, PATIENCE_MS);
    loadSiteContent().then((c) => { if (alive) setContent(c); });
    return () => { alive = false; clearTimeout(late); };
  }, []);
  return content;
}

/** What SiteLayout hands its pages. Outside it (a page rendered alone) it is the launch copy. */
export const SiteContentContext = createContext<SiteContent>(DEFAULT_CONTENT);

/** The document and a way to a shot by id, for a page inside SiteLayout. */
export function useSite() {
  const content = useContext(SiteContentContext);
  return { doc: content.doc, shot: (id: string) => shotOf(content, id) };
}

// ---------------------------------------------------------------------------
// The console's preview (Site content › Site › Preview)
//
// The console frames the real site at /?preview=draft (or /features…) and
// posts it the draft on every change; the page draws that instead of what
// is published, as an anonymous visitor would see it, and reports its height
// so the console can scale and scroll it. Nothing is fetched: a stranger who
// opens ?preview=draft sees the published site, and a message from any other
// window or origin is ignored.
// ---------------------------------------------------------------------------

export const PREVIEW_DRAFT = 'fm-site-preview';
export const PREVIEW_READY = 'fm-site-preview-ready';
export const PREVIEW_HEIGHT = 'fm-site-preview-height';

/** Is this page the console's preview — framed, and asked to draw a draft? */
export function isSitePreview(): boolean {
  if (typeof window === 'undefined' || window.parent === window) return false;
  return new URLSearchParams(window.location.search).get('preview') === 'draft';
}

/** The draft the console posts; undefined until the first one arrives. */
export function usePreviewContent(active: boolean): SiteContent | undefined {
  const [content, setContent] = useState<SiteContent>();
  useEffect(() => {
    if (!active) return undefined;
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== window.parent) return;
      const d = e.data as { type?: string; doc?: unknown; shots?: unknown } | null;
      if (!d || d.type !== PREVIEW_DRAFT) return;
      setContent({ doc: resolveDoc(d.doc), shots: toShots(d.shots) });
    };
    window.addEventListener('message', onMessage);
    window.parent.postMessage({ type: PREVIEW_READY }, window.location.origin);
    return () => window.removeEventListener('message', onMessage);
  }, [active]);
  return content;
}

/** Tell the console how tall the page is whenever that changes. */
export function usePreviewHeight(active: boolean, ref: RefObject<HTMLElement>) {
  useEffect(() => {
    const el = ref.current;
    if (!active || !el || typeof ResizeObserver === 'undefined') return undefined;
    const send = () => window.parent.postMessage({ type: PREVIEW_HEIGHT, height: el.scrollHeight }, window.location.origin);
    const ro = new ResizeObserver(send);
    ro.observe(el);
    send();
    return () => ro.disconnect();
  });
}
